"use client";

import Link from "next/link";
import { useParams, useSearchParams, useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import ChevronRight from "lucide-react/dist/esm/icons/chevron-right";
import AlertCircle from "lucide-react/dist/esm/icons/circle-alert";
import Clock from "lucide-react/dist/esm/icons/clock";
import Globe from "lucide-react/dist/esm/icons/globe";
import EyeOff from "lucide-react/dist/esm/icons/eye-off";
import Lock from "lucide-react/dist/esm/icons/lock";
import Settings2 from "lucide-react/dist/esm/icons/settings-2";
import SquarePen from "lucide-react/dist/esm/icons/square-pen";
import Archive from "lucide-react/dist/esm/icons/archive";
import CircleCheck from "lucide-react/dist/esm/icons/circle-check";
import Trash2 from "lucide-react/dist/esm/icons/trash-2";
import Send from "lucide-react/dist/esm/icons/send";
import { revalidateAssetDetail } from "@/app/actions/revalidate-asset-detail";
import { PublicPageShell } from "@/components/layout/PublicPageShell";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { DestructiveConfirmDialog } from "@/components/ui/destructive-confirm-dialog";
import { useAssetDetail } from "@/hooks/useAssetDetail";
import { useAssetAccess } from "@/hooks/useAssetAccess";
import { useDeleteAsset, useUpdateAssetStatus } from "@/hooks/useSellerAssets";
import { useAdminModeration } from "@/hooks/admin/useAdminModeration";
import { submittedStatus } from "@/lib/api/asset-input";
import { useDailyDownloadCount } from "@/hooks/useDailyDownloadCount";
import { ReferralProCard } from "@/components/growth/ReferralProCard";
import { useSession } from "@/contexts/SessionContext";
import { useProfile } from "@/contexts/ProfileContext";
import { AssetInfo } from "@/components/asset-detail/AssetInfo";
import { AssetPurchaseCard } from "@/components/asset-detail/AssetPurchaseCard";
import { SellerCard } from "@/components/asset-detail/SellerCard";
import { MobilePurchaseBar } from "@/components/asset-detail/MobilePurchaseBar";
import { PageUnavailable } from "@/components/asset-detail/PageUnavailable";
import { AssetHeader } from "@/components/asset-detail/AssetHeader";
import { AssetMediaStage } from "@/components/asset-detail/AssetMediaStage";
import { AssetSpecTable } from "@/components/asset-detail/AssetSpecTable";
import { ReviewsSection } from "@/components/asset-detail/ReviewsSection";
import { QuestionsSection } from "@/components/asset-detail/QuestionsSection";
import { trackViewItem } from "@/lib/analytics";
import { trackAssetAnalyticsEvent, trackSignupVerificationResumed } from "@/lib/analytics/events";
import { addRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { useAssetDownload } from "@/hooks/useAssetDownload";
import { consumePendingIntent, intentToTrigger, peekPendingIntent } from "@/lib/pending-intent";
import { isBuyerOnboardingPending, ONBOARDING_FINISHED_EVENT } from "@/lib/onboarding-gate";
import dynamic from "next/dynamic";
// Below-the-fold + on-interaction asset-detail sections — code-split so they
// don't ship in the initial bundle. The above-the-fold sticky purchase rail
// stays eager.
const UpsellSection = dynamic(
  () => import("@/components/asset-detail/UpsellSection").then((m) => ({ default: m.UpsellSection })),
);
const BundleOffer = dynamic(
  () => import("@/components/asset-detail/BundleOffer").then((m) => ({ default: m.BundleOffer })),
);
const RelatedProductsSection = dynamic(
  () => import("@/components/asset-detail/RelatedProductsSection").then((m) => ({ default: m.RelatedProductsSection })),
);
const ShareLinkModal = dynamic(
  () => import("@/components/sharing/ShareLinkModal").then((m) => ({ default: m.ShareLinkModal })),
  { ssr: false },
);
// Admin-only all-fields editor — lazy + only mounted when an admin opens it, so
// none of this admin code ships in the public asset-page bundle for buyers.
const AdminAssetEditSheet = dynamic(
  () => import("@/components/admin/AdminAssetEditSheet").then((m) => ({ default: m.AdminAssetEditSheet })),
  { ssr: false },
);
import { TrustBadgesDivider } from "@/components/ui/trust-badges";
import { SafeBoundary } from "@/components/SafeBoundary";
import { useWishlistToggle } from "@/hooks/useWishlistToggle";
import { logPlatformEvent } from "@/hooks/usePlatformEvents";
import { useCountdown } from "@/hooks/useCountdown";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { humanizeError } from "@/lib/error-humanizer";
import { listingPrice } from "@/lib/listing-price";

function AssetDetailSkeleton() {
  return (
    <div className="container mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-7 space-y-4">
          <Skeleton className="aspect-[4/3] w-full rounded-xl" />
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton
                key={i}
                className="w-20 h-16 rounded-lg shrink-0"
                style={{ animationDelay: `${i * 60}ms` } as React.CSSProperties}
              />
            ))}
          </div>
          <Skeleton className="h-20 w-full rounded-xl" />
        </div>
        <div className="lg:col-span-5 space-y-5">
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <Skeleton className="h-7 w-3/4" />
            <Skeleton className="h-9 w-1/3" />
            <div className="space-y-2.5">
              <Skeleton className="h-11 w-full rounded-lg" />
              <Skeleton className="h-11 w-full rounded-lg" />
            </div>
            <div className="space-y-2 pt-2 border-t border-border/30">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded shrink-0" />
                  <Skeleton className="h-4 w-32" />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl border border-border bg-card p-5 space-y-4">
            <div className="flex items-center gap-3">
              <Skeleton className="h-12 w-12 rounded-full shrink-0" />
              <div className="space-y-1.5 flex-1">
                <Skeleton className="h-5 w-32" />
                <Skeleton className="h-4 w-20" />
              </div>
            </div>
            <Skeleton className="h-9 w-full rounded-lg" />
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AssetDetail() {
  const { slug } = useParams<{ slug: string }>();
  const searchParams = useSearchParams();
  const { data: asset, isLoading, error } = useAssetDetail(slug || "");
  const { user } = useSession();
  const { profile, isAdmin } = useProfile();
  const router = useRouter();
  const deleteAsset = useDeleteAsset();
  const updateStatus = useUpdateAssetStatus();
  const adminModeration = useAdminModeration();
  const dailyDownload = useDailyDownloadCount();
  // `is_free`, not "free-claimable": must match useAssetActions, which shares
  // this query key (a no-minimum PWYW listing still goes through its amount input).
  const { data: accessData } = useAssetAccess(
    asset?.id,
    asset?.seller?.id,
    asset?.is_free ?? false
  );
  const { downloadAsset } = useAssetDownload();
  const { isInWishlist, isToggling: isTogglingWishlist, toggle: toggleWishlist } = useWishlistToggle({
    assetId: asset?.id || "",
    assetTitle: asset?.title,
    price: asset ? listingPrice(asset).amount : undefined,
  });

  // Derived state — safe to compute before asset arrives (will be falsy).
  // These are constants, not hooks; placed up here so the hook block stays
  // intact across loading -> data transitions (Rules of Hooks).
  const isOwner = !!(user && asset?.seller?.id && user.id === asset.seller.user_id);
  // Owner (seller) or an admin can manage this listing (edit/visibility/status/delete).
  const canControl = !!isOwner || isAdmin;
  const isPendingReview = asset?.status === "pending";
  const isRejected = asset?.status === "rejected";
  const isScheduled = asset?.status === "scheduled";

  // Countdown for scheduled listings. useCountdown accepts a nullable input
  // and must run on every render — keep above the early returns.
  const countdown = useCountdown(isScheduled ? asset?.scheduled_publish_at : null);

  // Visibility state for inline change. Initializer runs once at mount with
  // asset=undefined, the useEffect below syncs it once asset arrives.
  const [visibility, setVisibility] = useState<"public" | "unlisted" | "private">(
    (asset?.visibility as "public" | "unlisted" | "private" | undefined) ?? "public",
  );

  // Share link modal state
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [adminEditOpen, setAdminEditOpen] = useState(false);

  // Track recently viewed
  useEffect(() => {
    if (!asset) return;
    addRecentlyViewed({
      id: asset.id,
      slug: asset.slug,
      title: asset.title,
      image: asset.images?.[0]?.image_url || null,
      price: asset.price,
      isFree: asset.is_free,
      pricing_type: asset.pricing_type ?? null,
      sale_price: asset.sale_price ?? null,
      sale_starts_at: asset.sale_starts_at ?? null,
      sale_ends_at: asset.sale_ends_at ?? null,
    });
  }, [asset?.id]);

  // Track referral visits
  useEffect(() => {
    const refId = searchParams.get("ref");
    if (refId && asset?.id && refId !== profile?.id) {
      logPlatformEvent({
        event_type: "referral_visit",
        actor_profile_id: refId,
        target_asset_id: asset.id,
        metadata: { referrer_profile_id: refId, asset_slug: asset.slug },
      });
    }
  }, [asset?.id, searchParams]);

  // Re-check the held download the moment onboarding completes (the event
  // fires synchronously with the flow closing — no profile round-trip wait).
  const [onboardingGateTick, setOnboardingGateTick] = useState(0);
  useEffect(() => {
    const bump = () => setOnboardingGateTick((n) => n + 1);
    window.addEventListener(ONBOARDING_FINISHED_EVENT, bump);
    return () => window.removeEventListener(ONBOARDING_FINISHED_EVENT, bump);
  }, []);

  // Resume a pending *download* after signup (OAuth return or the email-verify
  // link landing back on this page). This page owns download resumes — the
  // license/entitlement flow lives here. Everything else (buy, cart, checkout,
  // subscribe…) is consumed by the global PendingIntentResumer, so only
  // consume when the stored intent is a download for THIS asset.
  //
  // Sequencing contract: for brand-new buyers the onboarding flow runs FIRST
  // (it shows the download as queued the whole way through) — so while
  // onboarding is still pending we HOLD the intent instead of consuming it,
  // and deliver the download the moment the flow finishes.
  useEffect(() => {
    if (!user || !asset) return;
    const peeked = peekPendingIntent();
    if (!peeked || peeked.kind !== "download" || peeked.assetId !== asset.id) return;
    // Wait for the profile before deciding — a null profile would read as
    // "not onboarding-eligible" and fire the download prematurely.
    if (!profile) return;
    if (isBuyerOnboardingPending(user, profile)) return; // held — released by the event above
    const intent = consumePendingIntent();
    if (!intent || intent.kind !== "download") return;
    trackSignupVerificationResumed({ action_type: intentToTrigger(intent), asset_id: intent.assetId });
    toast.success("Here's the download you signed up for 🎉", {
      description: `${asset.title} is starting now.`,
    });
    setTimeout(() => {
      downloadAsset(asset.id, undefined, asset.title);
    }, 800);
  }, [user, asset?.id, profile?.id, profile?.onboarding_completed_at, onboardingGateTick]);

  // Handle tip=success query param
  useEffect(() => {
    if (searchParams.get("tip") === "success") {
      toast.success("Thank you! Your tip was sent to the creator.");
    }
  }, []);

  useEffect(() => {
    if (!asset) return;
    // What a buyer would pay now — never a PWYW listing's suggested price, which
    // isn't a charge. PWYW reports its minimum (0 when there is none).
    const viewPrice = listingPrice(asset).amount;
    trackViewItem({
      id: asset.id,
      title: asset.title,
      price: viewPrice,
      category: asset.category?.name,
      seller: asset.seller?.display_name || asset.seller?.username || undefined,
    });
    if (asset.seller?.id) {
      trackAssetAnalyticsEvent({
        name: "asset_viewed",
        payload: {
          asset_id: asset.id,
          asset_slug: asset.slug,
          seller_id: asset.seller.id,
          price_cents: Math.round(viewPrice * 100),
          category_slug: asset.category?.slug ?? null,
        },
      });
    }
  }, [asset?.id]);

  // Sync local visibility state with server value once the asset loads.
  useEffect(() => {
    if (asset?.visibility) {
      setVisibility(asset.visibility as "public" | "unlisted" | "private");
    }
  }, [asset?.visibility]);

  if (isLoading) {
    return (
      <PublicPageShell mainAriaLabel="Asset details" bleed>
        <AssetDetailSkeleton />
      </PublicPageShell>
    );
  }

  if (error || !asset) {
    return (
      <PublicPageShell mainAriaLabel="Asset details" bleed>
        <PageUnavailable />
      </PublicPageShell>
    );
  }

  const handleVisibilityChange = async (newVisibility: "public" | "unlisted" | "private") => {
    if (!asset) return;
    setVisibility(newVisibility);
    try {
      const { error } = await supabase
        .from("assets")
        .update({ visibility: newVisibility })
        .eq("id", asset.id);
      if (error) throw error;
      // Visibility decides whether this page 404s for non-owners, so the SSR
      // cache has to drop with it.
      await revalidateAssetDetail(asset.slug);
      toast.success(`Visibility changed to ${newVisibility}`);
    } catch (err) {
      console.error("Failed to update visibility:", err);
      toast.error(humanizeError(err, "updating visibility"));
      setVisibility(asset.visibility ?? "public");
    }
  };

  // A seller's own status moves: take a live listing down, or send a listing
  // to review. Going live is the review team's decision (the DB refuses a
  // seller's "published"), and a future launch date parks it as scheduled —
  // the same choice the editor makes.
  const handleSellerStatusChange = async (status: "archived" | "pending" | "scheduled") => {
    if (!asset) return;
    try {
      await updateStatus.mutateAsync({ assetId: asset.id, status });
      toast.success(
        status === "archived"
          ? "Asset unpublished (archived)"
          : status === "scheduled"
            ? "Scheduled — it goes to review at its launch time"
            : "Submitted for review",
      );
    } catch (err) {
      toast.error(humanizeError(err, "updating the listing status"));
    }
  };

  // Staff go through the one audited decision path (moderation log, seller
  // notice, page revalidation); it reports its own outcome.
  const handleStaffStatusChange = (status: "published" | "archived") => {
    if (!asset) return;
    adminModeration.updateAssetStatus.mutate({ assetId: asset.id, status });
  };

  const sellerCanSubmit =
    asset.status === "draft" ||
    asset.status === "archived" ||
    asset.status === "rejected" ||
    asset.status === "changes_requested";

  const handleDeleteAsset = async () => {
    if (!asset) return;
    try {
      await deleteAsset.mutateAsync(asset.id);
      toast.success("Asset deleted");
      setDeleteOpen(false);
      router.push(isOwner ? "/seller/assets" : "/");
    } catch (err) {
      toast.error(humanizeError(err, "deleting the asset"));
    }
  };

  return (
    <>
      <PublicPageShell mainAriaLabel="Asset details" width="wide">
        {/* Scheduled Countdown Banner */}
        {isScheduled && !countdown.isExpired && (
          <Alert className="mb-6 border-primary/50 bg-primary/10">
            <Clock className="h-4 w-4 text-primary" />
            <AlertTitle className="text-primary">Scheduled Launch</AlertTitle>
            <AlertDescription className="text-primary/80 flex items-center gap-2">
              <span>This asset launches in</span>
              <span className="font-mono font-bold text-foreground text-base">{countdown.formatted}</span>
            </AlertDescription>
          </Alert>
        )}

        {/* Pending Review Banner - Only visible to asset owner */}
        {isOwner && isPendingReview && (
          <Alert className="mb-6 border-warning/50 bg-warning/10">
            <AlertCircle className="h-4 w-4 text-warning" />
            <AlertTitle className="text-warning">Asset Pending Review</AlertTitle>
            <AlertDescription className="text-warning/80">
              This asset is currently under review by our team. Only you can see this page until it's approved. 
              Review typically takes 1-2 business days.
            </AlertDescription>
          </Alert>
        )}

        {/* Rejected Banner - Only visible to asset owner */}
        {isOwner && isRejected && (
          <Alert className="mb-6 border-destructive/50 bg-destructive/10">
            <AlertCircle className="h-4 w-4 text-destructive" />
            <AlertTitle className="text-destructive">Asset Rejected</AlertTitle>
            <AlertDescription className="text-destructive/80">
              This asset was not approved for publication.
              {asset?.rejection_reason && (
                <span className="block mt-1">
                  <span className="font-medium">Reason:</span> {asset.rejection_reason}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6 overflow-x-auto scrollbar-none">
          <Link href="/" className="hover:text-foreground transition-colors">
            Home
          </Link>
          <ChevronRight className="h-4 w-4" />
          <Link href="/browse" className="hover:text-foreground transition-colors">
            Browse
          </Link>
          {asset.category && (
            <>
              <ChevronRight className="h-4 w-4" />
              <Link href={`/browse?category=${asset.category.slug}`}
                className="hover:text-foreground transition-colors"
              >
                {asset.category.name}
              </Link>
            </>
          )}
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground truncate max-w-[180px] sm:max-w-[250px]">{asset.title}</span>
        </nav>

        {/* Hero: 7 + 5 column grid (desktop) */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          <section aria-label="Media" className="min-w-0 lg:col-span-7 space-y-0">
            <AssetMediaStage asset={asset} />
          </section>

          <aside
            aria-label="Purchase options"
            className="min-w-0 lg:col-span-5 space-y-5 lg:sticky lg:top-20 lg:self-start"
          >
            <div className="flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <AssetHeader
                  asset={asset}
                  isOwner={!!isOwner}
                  isInWishlist={isInWishlist}
                  isTogglingWishlist={isTogglingWishlist}
                  onToggleWishlist={() => toggleWishlist()}
                  onShare={() => setShareModalOpen(true)}
                />
              </div>
              {canControl && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="shrink-0 gap-1.5">
                      <Settings2 className="h-3.5 w-3.5" />
                      Manage
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-60">
                    {isOwner && (
                      <>
                        <DropdownMenuItem asChild className="gap-2">
                          <Link href={`/seller/assets/${asset.id}/edit`}>
                            <SquarePen className="h-4 w-4" />
                            Edit asset
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}

                    {isAdmin && (
                      <>
                        <DropdownMenuItem onClick={() => setAdminEditOpen(true)} className="gap-2">
                          <SquarePen className="h-4 w-4" />
                          Edit all fields
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                      </>
                    )}

                    <DropdownMenuLabel>Status</DropdownMenuLabel>
                    {isAdmin ? (
                      asset.status === "published" ? (
                        <DropdownMenuItem onClick={() => handleStaffStatusChange("archived")} className="gap-2">
                          <Archive className="h-4 w-4 text-warning" />
                          Unpublish (archive)
                        </DropdownMenuItem>
                      ) : (
                        <DropdownMenuItem onClick={() => handleStaffStatusChange("published")} className="gap-2">
                          <CircleCheck className="h-4 w-4 text-success" />
                          Publish
                        </DropdownMenuItem>
                      )
                    ) : asset.status === "published" ? (
                      <DropdownMenuItem onClick={() => handleSellerStatusChange("archived")} className="gap-2">
                        <Archive className="h-4 w-4 text-warning" />
                        Unpublish (archive)
                      </DropdownMenuItem>
                    ) : sellerCanSubmit ? (
                      <DropdownMenuItem
                        onClick={() => handleSellerStatusChange(submittedStatus(asset.scheduled_publish_at))}
                        className="gap-2"
                      >
                        <Send className="h-4 w-4 text-primary" />
                        Submit for review
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem disabled className="gap-2">
                        <Clock className="h-4 w-4" />
                        {asset.status === "pending"
                          ? "Waiting for review"
                          : asset.status === "scheduled"
                            ? "Scheduled for review"
                            : "Suspended by moderation"}
                      </DropdownMenuItem>
                    )}

                    <DropdownMenuSeparator />
                    <DropdownMenuLabel>Visibility</DropdownMenuLabel>
                    <DropdownMenuItem onClick={() => handleVisibilityChange("public")} className="gap-2">
                      <Globe className="h-4 w-4 text-success" />
                      Public
                      {visibility === "public" && <CircleCheck className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleVisibilityChange("unlisted")} className="gap-2">
                      <EyeOff className="h-4 w-4 text-warning" />
                      Unlisted
                      {visibility === "unlisted" && <CircleCheck className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => handleVisibilityChange("private")} className="gap-2">
                      <Lock className="h-4 w-4 text-destructive" />
                      Private
                      {visibility === "private" && <CircleCheck className="ml-auto h-3.5 w-3.5 text-muted-foreground" />}
                    </DropdownMenuItem>

                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => setDeleteOpen(true)}
                      className="gap-2 text-destructive focus:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      Delete asset
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            <AssetQuickStats asset={asset} />

            {/* Free path to unlimited downloads — promoted to free-tier buyers
                (the Twitter-sourced free-download cohort), not owners. */}
            {user && !isOwner && dailyDownload.isLimited && (
              <SafeBoundary>
                <ReferralProCard compact />
              </SafeBoundary>
            )}

            <AssetPurchaseCard asset={asset} />

            <TrustBadgesDivider />

            {/* Non-critical sections degrade individually — a crash inside one
                hides just that section instead of tearing down the page. */}
            {asset.seller && (
              <SafeBoundary>
                <SellerCard seller={asset.seller} asset={{ id: asset.id, title: asset.title }} />
              </SafeBoundary>
            )}

            <SafeBoundary>
              <BundleOffer assetId={asset.id} isOwner={!!isOwner} />
            </SafeBoundary>

            <SafeBoundary>
              <UpsellSection assetId={asset.id} isOwner={!!isOwner} />
            </SafeBoundary>
          </aside>
        </div>

        {/* Below the fold: description, specs, reviews, Q&A */}
        <div className="mt-14 lg:mt-16 space-y-14 max-w-3xl">
          <AssetInfo asset={asset} hasAccess={!!user && !!accessData?.canDownload} />
          <AssetSpecTable asset={asset} />
          <section id="reviews" aria-label="Reviews" className="scroll-mt-28 min-w-0">
            <SafeBoundary>
              <ReviewsSection
                assetId={asset.id}
                assetSlug={asset.slug}
                avgRating={asset.avg_rating}
                reviewCount={asset.review_count}
                isAssetOwner={!!isOwner}
                canReview={!!accessData?.hasPurchased}
              />
            </SafeBoundary>
          </section>
          {asset.seller?.id ? (
            <section id="questions" aria-label="Questions and answers" className="scroll-mt-28 min-w-0">
              <SafeBoundary>
                <QuestionsSection assetId={asset.id} sellerId={asset.seller.id} />
              </SafeBoundary>
            </section>
          ) : null}
        </div>

        {/* Related products */}
        <SafeBoundary>
          <RelatedProductsSection
            assetId={asset.id}
            categoryName={asset.category?.name}
            categorySlug={asset.category?.slug}
            sellerId={asset.seller?.id}
            sellerName={asset.seller?.display_name || asset.seller?.username}
            sellerUsername={asset.seller?.username}
          />
        </SafeBoundary>
      </PublicPageShell>

      {/* Mobile Sticky Purchase Bar */}
      <MobilePurchaseBar asset={asset} />

      {/* Share link modal */}
      <ShareLinkModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        destinationUrl={`https://uefnmarketplace.com/asset/${asset.slug}`}
        entityType="asset"
        entityId={asset.id}
        entitySlug={asset.slug}
        entityName={asset.title}
      />

      {canControl && (
        <DestructiveConfirmDialog
          open={deleteOpen}
          onOpenChange={setDeleteOpen}
          title="Delete this asset?"
          description="This permanently deletes the asset and its images, reviews, and questions. To keep it off the marketplace without losing buyers' access, use Unpublish (archive) instead. This cannot be undone."
          confirmLabel="Delete asset"
          onConfirm={handleDeleteAsset}
          isPending={deleteAsset.isPending}
        />
      )}

      {isAdmin && adminEditOpen && (
        <AdminAssetEditSheet
          assetId={asset.id}
          open={adminEditOpen}
          onOpenChange={setAdminEditOpen}
        />
      )}
    </>
  );
}

// Quick stats component for the right side
import Star from "lucide-react/dist/esm/icons/star";
import ShoppingBag from "lucide-react/dist/esm/icons/shopping-bag";
import Eye from "lucide-react/dist/esm/icons/eye";
import type { AssetDetail as AssetDetailType } from "@/hooks/useAssetDetail";

function AssetQuickStats({ asset }: { asset: AssetDetailType }) {
  return (
    <div className="flex flex-wrap items-center gap-4 text-sm">
      {asset.avg_rating !== null && (
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-warning/10">
          <Star className="h-4 w-4 fill-warning text-warning" />
          <span className="font-semibold text-foreground">{asset.avg_rating.toFixed(1)}</span>
          <span className="text-muted-foreground">({asset.review_count})</span>
        </div>
      )}
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <ShoppingBag className="h-4 w-4" />
        <span>{asset.download_count?.toLocaleString() || 0} downloads</span>
      </div>
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Eye className="h-4 w-4" />
        <span>{asset.view_count?.toLocaleString() || 0} views</span>
      </div>
    </div>
  );
}
